import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ProposalTrackingData, ProposalTrackingHelper } from '../models/proposal-tracking.model';
import { ReturnResult } from '../models/return-result';

@Injectable({
  providedIn: 'root'
})
export class ProposalTrackingStateService {
  private baseUrl = environment.apiSaleOpportunity;

  private currentTrackingSubject = new BehaviorSubject<{ [opportunityId: string]: ProposalTrackingData }>({});
  public currentTracking$ = this.currentTrackingSubject.asObservable();

  constructor(private http: HttpClient) {}

  public getTracking(opportunityId: string): ProposalTrackingData {
    return this.currentTrackingSubject.value[opportunityId] || ProposalTrackingHelper.defaultTrackingData();
  }

  public setTracking(opportunityId: string, data: any): ProposalTrackingData {
    const parsed = ProposalTrackingHelper.parse(data);
    const current = { ...this.currentTrackingSubject.value, [opportunityId]: parsed };
    this.currentTrackingSubject.next(current);
    return parsed;
  }

  public getProposalTracking(opportunityId: string): Observable<ReturnResult<ProposalTrackingData>> {
    return this.http.get<ReturnResult<ProposalTrackingData>>(`${this.baseUrl}/GetProposalTracking/${opportunityId}`);
  }

  public refreshTracking(opportunityId: string): Observable<ProposalTrackingData> {
    if (!opportunityId) {
      return of(ProposalTrackingHelper.defaultTrackingData());
    }

    // Call dedicated lightweight tracking endpoint
    return this.http.get<ReturnResult<ProposalTrackingData>>(`${this.baseUrl}/GetProposalTracking/${opportunityId}`).pipe(
      map(res => {
        const parsed = ProposalTrackingHelper.parse(res?.result);
        this.setTracking(opportunityId, parsed);
        return parsed;
      }),
      catchError(err => {
        console.warn('Could not refresh proposal tracking status:', err);
        return of(this.getTracking(opportunityId));
      })
    );
  }

  public markAsSent(opportunityId: string): Observable<boolean> {
    if (!opportunityId) {
      return of(false);
    }

    return this.http.post<ReturnResult<boolean>>(`${this.baseUrl}/MarkProposalSent/${opportunityId}`, {}).pipe(
      map(res => {
        const isSuccess = Boolean(res?.result);
        if (isSuccess) {
          const current = this.getTracking(opportunityId);
          current.isSent = true;
          current.sentAt = new Date().toISOString();
          this.setTracking(opportunityId, current);
        }
        return isSuccess;
      }),
      catchError(err => {
        console.warn('Could not mark proposal as sent on backend:', err);
        return of(false);
      })
    );
  }
}
